import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from 'reactstrap';
import Pagination from './Pagination';

const ReportedListingsDisplay = ({ statusFilter = '', searchQuery = '', onListingClick }) => {
  const [reportedListings, setReportedListings] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  const fetchReportedListings = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 50,
      };

      // Add status filter if provided
      if (statusFilter) {
        params.status = statusFilter;
      }

      // Add search query if provided
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await axios.get(`${API_BASE_URL}/admin/reported-listings`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (response.data.data && response.data.pagination) {
        setReportedListings(response.data.data);
        setPagination(response.data.pagination);
      } else {
        // Fallback for old API format
        setReportedListings(Array.isArray(response.data) ? response.data : []);
        setPagination(null);
      }
    } catch (err) {
      console.error('Failed to fetch reported listings:', err);
      setError('Failed to fetch reported listings');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchReportedListings(page);
  };

  // Reset to page 1 when status filter or search changes
  useEffect(() => {
    setCurrentPage(1);
    fetchReportedListings(1);
  }, [statusFilter, searchQuery]);

  // Status badge helper function
  const getStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { bg: '#ffc107', color: '#000', text: 'Pending' },
      'REMOVED': { bg: '#dc3545', color: '#fff', text: 'Removed' },
      'DISMISSED': { bg: '#6c757d', color: '#fff', text: 'Dismissed' },
      'RESOLVED': { bg: '#28a745', color: '#fff', text: 'Resolved' },
    };
    const config = statusConfig[status] || statusConfig['PENDING'];
    return (
      <span style={{ 
        backgroundColor: config.bg, 
        color: config.color, 
        padding: '4px 10px', 
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 'bold',
        textTransform: 'uppercase'
      }}>
        {config.text}
      </span>
    );
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <div>
      <table
        cellPadding="8"
        cellSpacing="0"
        style={{ width: '100%', marginTop: '20px', borderCollapse: 'collapse' }}
      >
        <thead style={{
          backgroundColor: 'rgb(250, 248, 255)',
          borderTop: '1px solid rgb(233, 230, 241)',
          borderBottom: '1px solid rgb(233, 230, 241)',
        }}>
          <tr>
            <th className="p-3 text-center">Listing</th>
            <th className="p-3 text-center">Seller</th>
            <th className="p-3 text-center">Reports</th>
            <th className="p-3 text-center">Status</th>
            <th className="p-3 text-center">First Reported</th>
            <th className="p-3 text-center">Last Reported</th>
            <th className="p-3 text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          {reportedListings.length === 0 ? (
            <tr>
              <td colSpan="7" className="p-2 text-center">
                No reported listings found
              </td>
            </tr>
          ) : (
            reportedListings.map((item) => {
              const imageUrl = Array.isArray(item.listing?.images) && item.listing.images[0]?.fileUrl;
              const status = item.overallStatus || 'PENDING';

              return (
                <tr className='border-bottom' key={item.listingId}>
                  <td style={{ padding: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={item.listing?.title}
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          background: '#ccc',
                          borderRadius: '4px',
                        }}
                      />
                    )}
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{item.listing?.title || 'N/A'}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {item.listing?.category || 'N/A'} • ${item.listing?.price || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="p-2 text-center">
                    <div>{item.listing?.seller?.fullName || 'N/A'}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {item.listing?.seller?.email || 'N/A'}
                    </div>
                  </td>
                  <td className="p-2 text-center">
                    <span style={{ 
                      backgroundColor: '#ff4444', 
                      color: 'white', 
                      padding: '4px 8px', 
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {item.totalReports}
                    </span>
                  </td>
                  <td className="p-2 text-center">
                    {getStatusBadge(status)}
                  </td>
                  <td className="p-2 text-center">
                    {new Date(item.firstReportedAt).toLocaleDateString()}
                  </td>
                  <td className="p-2 text-center">
                    {new Date(item.lastReportedAt).toLocaleDateString()}
                  </td>
                  <td className="p-2 text-center">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => onListingClick && onListingClick(item)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {pagination && (
        <Pagination pagination={pagination} onPageChange={handlePageChange} />
      )}
    </div>
  );
};

export default ReportedListingsDisplay;
